import type { Fragment } from '@atlaskit/editor-prosemirror/model';
import { Node } from '@atlaskit/editor-prosemirror/model';

export type ContextSelectionScope = 'fullDocument' | 'partialSelection' | 'none';

export type ContentStatistics = {
	nodeCounts: Record<string, number>;
	markCounts: Record<string, number>;
	linksCount: number;
	mentionsCount?: number;
	totalCharacterCount: number;
	contextSelectionScope?: ContextSelectionScope;
};

export class ProseMirrorContentStatistics {
	private content: Node | Fragment;

	constructor(content: Node | Fragment) {
		this.content = content;
	}

	/**
	 * Calculate statistics for the ProseMirror content such as node counts, mark counts and total character count.
	 */
	public collectStatistics(): ContentStatistics {
		const { nodeCounts, markCounts, linksCount, mentionsCount } = this.countNodesAndMarks();
		const { totalCharacterCount } = this.calculateCharacterCounts();
		return {
			nodeCounts,
			markCounts,
			linksCount,
			mentionsCount,
			totalCharacterCount,
			contextSelectionScope: undefined,
		};
	}

	private countNodesAndMarks(): {
		nodeCounts: Record<string, number>;
		markCounts: Record<string, number>;
		linksCount: number;
		mentionsCount?: number;
	} {
		const nodeCounts: Map<string, number> = new Map();
		const markCounts: Map<string, number> = new Map();
		let linksCount = 0;
		let mentionsCount = 0;

		this.content.descendants((node) => {
			nodeCounts.set(node.type.name, (nodeCounts.get(node.type.name) || 0) + 1);

			const marks = node.marks;
			marks.forEach((mark) => {
				markCounts.set(mark.type.name, (markCounts.get(mark.type.name) || 0) + 1);
			});

			const {
				nodes: { inlineCard, mention },
				marks: { link },
			} = node.type.schema;

			if (node.type === mention) {
				mentionsCount++;
			}
			if (node.type === inlineCard && node.attrs.url) {
				linksCount++;
			}
			if (marks.find((mark) => mark.type === link)) {
				linksCount++;
			}
		});

		return {
			nodeCounts: Object.fromEntries(nodeCounts),
			markCounts: Object.fromEntries(markCounts),
			linksCount,
			mentionsCount,
		};
	}

	private calculateCharacterCounts(): {
		totalCharacterCount: number;
	} {
		let allTextContent = '';
		if (this.content instanceof Node) {
			allTextContent = this.content.textContent;
		} else {
			this.content.forEach((child) => {
				allTextContent += child.textContent;
			});
		}
		return {
			totalCharacterCount: allTextContent.length,
		};
	}
}
